/**
 * Incident Entity Components - Maps entity types to display labels. Single
 * source of truth for entity labels across the app.
 */

/** Entity label configuration: singular and plural forms */
const ENTITY_LABELS: Record<string, { singular: string; plural: string }> = {
  // Core incident
  incident_metadata: { singular: 'Incident', plural: 'Incident Info' },
  incident_info: { singular: 'Incident Info', plural: 'Incident Info' },
  // Strategic planning
  objective: { singular: 'Objective', plural: 'Objectives' },
  strategy: { singular: 'Strategy', plural: 'Strategies' },
  tactic: { singular: 'Tactic', plural: 'Tactics' },
  action: { singular: 'Action', plural: 'Actions' },
  // Resources
  resource_set: { singular: 'Resource', plural: 'Resources' },
  incident_resource: {
    singular: 'Resource Requirement',
    plural: 'Resource Requirements',
  },
  resource_request: {
    singular: 'Resource Request',
    plural: 'Resource Requests',
  },
  strike_team: { singular: 'Strike Team', plural: 'Strike Teams' },
  resource_in_incident: {
    singular: 'Checked-In Resource',
    plural: 'Checked-In Resources',
  },
  // Operations
  work_assignment: { singular: 'Work Assignment', plural: 'Work Assignments' },
  // Medical
  hospital: { singular: 'Hospital', plural: 'Hospitals' },
  medical_transportation: {
    singular: 'Medical Transportation',
    plural: 'Medical Transportation',
  },
  medical_aid_station: {
    singular: 'Medical Aid Station',
    plural: 'Medical Aid Stations',
  },
  // Communications
  coms: { singular: 'Communications', plural: 'Communications' },
  // Planning
  calendar_event: { singular: 'Calendar Event', plural: 'Calendar Events' },
  member: { singular: 'Incident Member', plural: 'Members' },
  // Legacy fallback
  full_incident: { singular: 'Incident Data', plural: 'Incident Data' },
};

/** Get human-readable singular label for an entity type */
export function getEntityTypeLabel(entityType: string): string {
  return ENTITY_LABELS[entityType]?.singular || entityType || 'Data';
}

/** Get human-readable plural label for an entity type */
export function getEntityTypePluralLabel(entityType: string): string {
  return ENTITY_LABELS[entityType]?.plural || entityType || 'Data';
}

/** Entity types that have dedicated edit/view drawers in the IAP */
export const SUPPORTED_ENTITY_TYPES = [
  'objective',
  'strategy',
  'tactic',
  'action',
  'resource_set',
  'resource_request',
  'work_assignment',
  'hospital',
  'medical_transportation',
  'medical_aid_station',
  'coms',
  'calendar_event',
  'incident_info',
  'member',
  'strike_team',
] as const;

export type SupportedEntityType = (typeof SUPPORTED_ENTITY_TYPES)[number];

/** Check if an entity type has a supported viewer component */
export function isEntityTypeSupported(
  entityType: string
): entityType is SupportedEntityType {
  return SUPPORTED_ENTITY_TYPES.includes(entityType as SupportedEntityType);
}
